import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Award, Shield, CheckCircle } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://www.echt.gold';

const Uhrenankauf = () => {
  const brands = [
    'Rolex', 'Omega', 'Patek Philippe', 'Audemars Piguet', 'Breitling', 
    'IWC', 'Cartier', 'Tag Heuer', 'Jaeger-LeCoultre', 'Panerai',
    'Hublot', 'Vacheron Constantin', 'A. Lange & Söhne', 'Breguet', 'Chopard'
  ];

  return (
    <>
      <Helmet>
        <title>Uhrenankauf - Echt Gold | Rolex, Omega & Luxusuhren</title>
        <meta name="description" content="Luxusuhren-Ankauf: Rolex, Omega, Patek Philippe. Faire Preise, diskrete Abwicklung. Mit oder ohne Papiere. Jetzt bewerten lassen!" />
        <link rel="canonical" href={`${BASE_URL}/uhrenankauf`} />
      </Helmet>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Home", "item": `${BASE_URL}/`},
            {"@type": "ListItem", "position": 2, "name": "Uhrenankauf", "item": `${BASE_URL}/uhrenankauf`}
          ]
        })}
      </script>

      <div className="bg-black/90 py-8 border-b border-gold/20">
        <div className="container mx-auto px-4">
          <div className="text-sm text-gray-400 mb-2">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-gold">Uhrenankauf</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold gold-text">Uhrenankauf</h1>
        </div>
      </div>

      <section className="section section-dark">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold mb-6 gold-text">Luxusuhren zu Höchstpreisen verkaufen</h2>
            <p className="text-gray-300 text-lg mb-6">
              Sie möchten Ihre hochwertige Armbanduhr verkaufen? Wir kaufen Luxusuhren aller renommierten Marken - mit oder ohne Box und Papiere.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-gold/10 px-4 py-2 rounded-full border border-gold/30">
                <Award className="text-gold" size={20} />
                <span>Faire Marktpreise</span>
              </div>
              <div className="flex items-center gap-2 bg-gold/10 px-4 py-2 rounded-full border border-gold/30">
                <Shield className="text-gold" size={20} />
                <span>Diskrete Abwicklung</span>
              </div>
              <div className="flex items-center gap-2 bg-gold/10 px-4 py-2 rounded-full border border-gold/30">
                <CheckCircle className="text-gold" size={20} />
                <span>Schnelle Auszahlung</span>
              </div>
            </div>
          </div>

          <div className="card max-w-5xl mx-auto">
            <h3 className="text-2xl font-bold mb-6 gold-text text-center">Diese Marken kaufen wir an</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {brands.map((brand, idx) => (
                <div key={idx} className="text-center p-3 bg-black/40 rounded-lg border border-gold/20 hover:border-gold/50 transition-colors">
                  <span className="text-gray-300">{brand}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-400 mt-6 text-sm">
              ... und viele weitere Premium-Marken
            </p>
          </div>
        </div>
      </section>

      <section className="section section-darker">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center gold-text">Was macht unseren Uhrenankauf besonders?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="card">
                <h3 className="text-xl font-bold mb-3 gold-text">Expertenbewertung</h3>
                <p className="text-gray-300">
                  Unsere Uhrenexperten kennen den Markt und bewerten Ihre Uhr fair und transparent. Wir berücksichtigen Zustand, Seltenheit und aktuelle Marktwerte.
                </p>
              </div>
              <div className="card">
                <h3 className="text-xl font-bold mb-3 gold-text">Mit oder ohne Papiere</h3>
                <p className="text-gray-300">
                  Wir kaufen Luxusuhren auch ohne Box und Papiere an. Natürlich erhöhen vollständige Sets mit Original-Dokumentation den Wert.
                </p>
              </div>
              <div className="card">
                <h3 className="text-xl font-bold mb-3 gold-text">Sofortige Auszahlung</h3>
                <p className="text-gray-300">
                  Nach erfolgreicher Prüfung und Ihrer Zustimmung zum Angebot erfolgt die Auszahlung sofort - bar oder per Überweisung.
                </p>
              </div>
              <div className="card">
                <h3 className="text-xl font-bold mb-3 gold-text">Diskret & Sicher</h3>
                <p className="text-gray-300">
                  Alle Transaktionen werden vertraulich behandelt. Auf Wunsch kommen wir auch zu Ihnen nach Hause (bei hochwertigen Uhren).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-dark">
        <div className="container">
          <div className="card text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 gold-text">Ihre Luxusuhr verkaufen?</h2>
            <p className="text-gray-300 mb-8 text-lg">
              Vereinbaren Sie einen diskreten Termin für die Bewertung Ihrer Uhr
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/kontakt" className="btn-primary" data-testid="cta-uhrenankauf">Jetzt Termin vereinbaren</Link>
              <a href="tel:023139085116" className="btn-secondary">0231 39085116</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Uhrenankauf;